(ns frontend.view.modals.entity.schema
  (:require
   [clojure.string :as string]
   [common.localization :as loc]
   [common.schema.data-types :as data-types]
   [common.schema.field-spec :as field-spec]
   [common.schema.registry :as registry]
   [common.util :as cu]
   [frontend.state.current-session :as session]
   [frontend.tenant-customizations :as tenant-customizations]
   [potpuri.core :as potpuri]))

(defmulti raw-schema
  (fn [{:keys [entity-type]}]
    entity-type))

(defn- create-editor-type
  [field-schema]
  (->> field-schema
       :field/data-type
       (get data-types/zapflow-data-types)
       :create-type))

(defn- hidden-or-link-editor?
  [field-schema]
  (contains? #{:editor.type/none
               :editor.type/link}
             (-> field-schema
                 :field/default-visibility
                 :editor)))

(defn- legacy-create-mandatory-editor-overrides
  "Create forms only lift legacy mandatory hidden/link fields into editable
  controls. Target system-required semantics are broader and include generated
  system fields, so they are intentionally not used here."
  [schema]
  (->> schema
       (keep (fn [[attribute field-schema]]
               (when (and (field-spec/legacy-always-mandatory-field? field-schema)
                          (hidden-or-link-editor? field-schema))
                 [attribute
                  {:field/default-visibility {:editor (create-editor-type field-schema)}}])))
       (into {})))

(defmethod raw-schema :default
  [{:keys [entity-type schema]}]
  (apply dissoc
         (potpuri/deep-merge
          schema
          (legacy-create-mandatory-editor-overrides schema)
          (let [name-attr (registry/resolve-name-attribute entity-type)]
            (if name-attr
              {name-attr {:field/default-visibility {:editor :editor.type/text-simple}}}
              {})))
         (->> schema
              (filter (fn [[_ fs]]
                        (:field/not-shown-in-create? fs)))
              (keys))))

(defmethod raw-schema :entity.type/sale-agreement
  [{:keys [entity-type schema]}]
  (let [hide-keys (->> schema
                       (filter (fn [[_ fs]]
                                 (:field/not-shown-in-create? fs)))
                       (keys))
        create-mandatory
        (legacy-create-mandatory-editor-overrides schema)
        name-attr (if-let [name-attr (registry/resolve-name-attribute entity-type)]
                    {name-attr {:field/default-visibility {:editor :editor.type/text-simple}}}
                    {})
        s (apply dissoc
                 (potpuri/deep-merge schema
                                     create-mandatory
                                     name-attr)
                 hide-keys)]
    s))

(defmethod raw-schema :entity.type/fund
  [{:keys [schema]}]
  (potpuri/deep-merge
   schema
   {:fund/name
    {:field/default-visibility {:editor :editor.type/text-simple}}
    :fund/teams
    {:field/default-visibility {:editor :editor.type/multi-select}}
    :fund/reporting-admins
    {:field/default-visibility {:editor :editor.type/multi-select}}
    :fund/feeder-fund-to
    {:field/default-visibility {:editor :editor.type/multi-select}}}))

(defmethod raw-schema :entity.type/investor
  [{:keys [schema]}]
  (potpuri/deep-merge
   schema
   (merge
    {:investor/legal-entity {:field/default-visibility {:editor :editor.type/select}}}
    (when (tenant-customizations/tesi?)
      {:investor/focus-geography {:field/default-visibility {:editor :editor.type/multi-select}}
       :investor/focus-sector    {:field/default-visibility {:editor
                                                             :editor.type/multi-select}}}))))

(defmethod raw-schema :entity.type/pipeline
  [{:keys [schema target-assets-in-use start-value]}]
  (potpuri/deep-merge
   schema
   {:pipeline/name {:field/default-visibility {:editor :editor.type/text-simple}}
    :pipeline/target-asset-types
    {:field/default-visibility (if target-assets-in-use
                                 {:editor :editor.type/multi-select}
                                 {:editor :editor.type/none})
     :field/translations       {:en (case (:pipeline/entity-type start-value)
                                      :entity.type/sales-opportunity
                                      (loc/tr :customer-types)
                                      (loc/tr :pipeline/target-asset-types))}
     :field/mandatory          (if target-assets-in-use
                                 :mandatory/always
                                 false)}}))

(defmethod raw-schema :entity.type/real-estate
  [{:keys [schema]}]
  (potpuri/deep-merge
   schema
   {:asset/name
    {:field/default-visibility {:editor :editor.type/text-simple}}}))

(defmethod raw-schema :entity.type/webentry
  [{:keys [schema]}]
  schema)

(defmethod raw-schema :entity.type/named-value
  [{:keys [schema named-value-attribute]}]
  (potpuri/deep-merge
   schema
   {:named-value/value
    {:named-value-attribute named-value-attribute}}))

(defmethod raw-schema :entity.type/team
  [{:keys [schema]}]
  (dissoc schema :team/members))

(defmethod raw-schema :entity.type/stage
  [{:keys [schema]}]
  schema)

(defmethod raw-schema :entity.type/opportunity-stage
  [{:keys [schema]}]
  schema)

(defmethod raw-schema :entity.type/sales-opportunity-stage
  [{:keys [schema]}]
  schema)

(defmethod raw-schema :entity.type/exit-opportunity-stage
  [{:keys [schema]}]
  schema)

(defmethod raw-schema :entity.type/fof-asset
  [{:keys [schema]}]
  (potpuri/deep-merge
   schema
   {:asset/name
    {:field/default-visibility {:editor :editor.type/text-simple}}}))

(defmethod raw-schema :entity.type/company
  [{:keys [schema]}]
  (potpuri/deep-merge
   schema
   (merge
    {:asset/name {:field/default-visibility {:editor :editor.type/text-simple}}}
    (cond
      (tenant-customizations/tesi?)
      {:company/tag-type       {:field/default-visibility {:editor :editor.type/multi-select}}
       :company/tag-tags       {:field/default-visibility {:editor :editor.type/multi-select}}
       :company/tag-portfolios {:field/default-visibility {:editor
                                                           :editor.type/multi-select}}}
      (tenant-customizations/f-log?)
      {:company/tag-tags {:field/default-visibility {:editor :editor.type/multi-select}}}))))

(defmethod raw-schema :entity.type/person
  [{:keys [schema]}]
  (potpuri/deep-merge
   schema
   {:person/tag-industry-experience {:field/default-visibility {:editor :editor.type/multi-select}}
    :person/tag-contact-priority    {:field/default-visibility {:editor :editor.type/multi-select}}
    :person/universal-email-address {:field/mandatory false}}))

(defmethod raw-schema :entity.type/holding
  [{:keys [schema deal-pipeline?]}]
  (dissoc (potpuri/deep-merge
           schema
           {:holding/name         {:field/default-visibility {:editor :editor.type/text-simple}}
            :holding/tag-tags     {:field/default-visibility {:editor :editor.type/multi-select}}
            :holding/target-asset {:field/default-visibility {:editor :editor.type/select}
                                   :field/entity-types       (session/available-target-assets
                                                              deal-pipeline?)
                                   :create-on-fly?           true}})
          (when-not (tenant-customizations/tesi?)
            :holding/tag-tags)))

(defmethod raw-schema :entity.type/survey-profile
  [{:keys [schema start-value]}]
  (let [survey-type (:survey-profile/type start-value)
        survey-type (if (:db/id survey-type)
                      survey-type
                      (session/get-entity survey-type))]
    (dissoc (potpuri/deep-merge
             schema
             {:survey-profile/target {:field/default-visibility {:editor :editor.type/select}
                                      :field/entity-types       (:survey-type/entity-types survey-type)
                                      :create-on-fly?           true}})
            :survey-profile/survey-processes
            :survey-profile/our-contact
            :survey-profile/their-contact
            :survey-profile/status)))

(defn target-asset-type-label
  [entity-type]
  (case entity-type
    :entity.type/real-estate (loc/tr :target-asset-real-estate)
    (loc/tr :target-asset-named [(string/lower-case (loc/tr entity-type))])))

(defn target-asset-type-placeholder
  [entity-type]
  (case entity-type
    :entity.type/real-estate (loc/tr :target-asset-real-estate-placeholder)
    (loc/tr :target-asset-named-placeholder
            [(string/lower-case (target-asset-type-label entity-type))])))

;; returns nil if the default "Target asset" should be kept - use with 'merge':
(defn target-asset-translations-schema
  [target-asset-entity-types]
  (when (cu/one-element? target-asset-entity-types)
    (let [target-asset-type (first target-asset-entity-types)]
      {:field/translations {:en (target-asset-type-label target-asset-type)}
       :placeholder        (target-asset-type-placeholder target-asset-type)})))

(defmethod raw-schema :entity.type/oppo
  [{:keys [schema capman-re? target-asset-entity-types]}]
  (dissoc
   (potpuri/deep-merge
    schema
    (merge {:oppo/target-asset
            (merge
             {:field/default-visibility {:editor :editor.type/select}
              :field/entity-types       (if (seq target-asset-entity-types)
                                          target-asset-entity-types
                                          (session/available-target-assets true))
              :create-on-fly?           true}
             (target-asset-translations-schema target-asset-entity-types))
            :oppo/stage        {:field/default-visibility {:editor :editor.type/select}}}))
   :oppo/on-hold-until
   (when capman-re?
     :oppo/lead-source-v2)
   (when capman-re?
     :oppo/target-asset)))

(defmethod raw-schema :entity.type/fund-commitment
  [{:keys [schema]}]
  (dissoc (potpuri/deep-merge
           schema
           {:fund-commitment/investor {:field/default-visibility {:editor :editor.type/select}
                                       :create-on-fly? true}})
          :fund-commitment/name))
